import {
  type ArtistDTO,
  formatArtists,
  pickI18n,
  type PlaylistDTO,
  type SearchResponse,
  type SongDTO,
} from '@akpl/shared';
import {
  colorFor,
  computeWindow,
  CoverImage,
  cx,
  FocusContext,
  getCurrentFocusKey,
  IconNote,
  IconSearch,
  OnScreenKeyboard,
  setFocus,
  useFocusable,
} from '@akpl/ui-tv';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { analytics } from '../analytics';
import { api } from '../api';
import { useRouter } from '../router';
import { PhoneQr } from '../shell/PhoneQr';
import { useSongActions } from '../song/songActions';
import { useT } from '../useT';
import { listFocusRepair, listIsFocusable, SEARCH_LIST, SEARCH_MORE_FOCUS } from './listFocus';
import { playlistTarget } from './Playlist';
import styles from './screens.module.css';
import type { ScreenProps } from './types';
import { useScreenFocus } from './useScreenFocus';

export const SEARCH_FOCUS = 'osk-A';
/** The tab strip, as a focus container that remembers which tab the results belong to. */
export const SEARCH_TABS_FOCUS = 'search-tabs';
/** docs/04 §3: one request, debounced 120 ms from the **first** character. */
export const SEARCH_DEBOUNCE_MS = 120;

type Tab = 'songs' | 'artists' | 'playlists';
const TABS: Tab[] = ['songs', 'artists', 'playlists'];
const EMPTY: SearchResponse = { songs: [], artists: [], playlists: [] };
/**
 * UX-047: how close to the end of the loaded songs the focus has to get before the next page
 * is fetched on its own. The explicit "Ver más" row is the *reachable* control; this only
 * saves the user a press when they walk the list to the bottom.
 */
const PREFETCH_MARGIN = 5;

function TabButton({
  tab,
  label,
  active,
  onSelect,
  onArrowPress,
}: {
  tab: Tab;
  label: string;
  active: boolean;
  onSelect: () => void;
  /** Left/Right between tabs (see `Search`); return `false` to consume the press. */
  onArrowPress?: (direction: string) => boolean;
}) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey: `search-tab-${tab}`,
    onEnterPress: onSelect,
    onFocus: onSelect,
    onArrowPress: (direction) => onArrowPress?.(direction) ?? true,
  });
  return (
    <div
      ref={ref}
      role="tab"
      aria-selected={active}
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={`search-tab-${tab}`}
      className={cx(styles.tab, active && styles.tabActive, focused && styles.tabFocused)}
    >
      {label}
    </div>
  );
}

interface ResultRowProps {
  focusKey: string;
  title: string;
  subtitle?: string;
  color?: string;
  imageUrl?: string | null;
  /** Pink "OK · elegir tono" cue, shown on the focused song row (Figma TV-04 67:2544). */
  cue?: string;
  onSelect: () => void;
  onFocus: () => void;
  /** Left/Right inside a result list hop to the neighbouring tab (see `Search`). */
  onArrowPress?: (direction: string) => boolean;
}

function ResultRow({
  focusKey,
  title,
  subtitle,
  color,
  imageUrl,
  cue,
  onSelect,
  onFocus,
  onArrowPress,
}: ResultRowProps) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey,
    onEnterPress: onSelect,
    onFocus,
    onArrowPress: (direction) => onArrowPress?.(direction) ?? true,
  });
  return (
    <div
      ref={ref}
      role="button"
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={focusKey}
      className={cx(styles.resultRow, focused && styles.resultRowFocused)}
    >
      <span className={styles.resultCover} style={{ background: color ?? 'var(--akp-surface-3)' }}>
        <CoverImage src={imageUrl} width={72} height={72} fallback={<IconNote size={32} />} />
      </span>
      <span className={styles.resultInfo}>
        <div className={styles.resultTitle}>{title}</div>
        {subtitle ? <div className={styles.resultSub}>{subtitle}</div> : null}
      </span>
      {cue && focused ? <span className={styles.resultCue}>{cue}</span> : null}
    </div>
  );
}

/**
 * "Ver más" at the end of the song results (UX-047). It is a focusable leaf inside the result
 * scroller, so it is reachable with the D-pad exactly like a row: Down off the last song lands
 * on it, Enter (or simply arriving on it) loads the next page and appends it.
 */
function MoreRow({
  label,
  loading,
  onSelect,
  onFocus,
  onArrowPress,
}: {
  label: string;
  loading: boolean;
  onSelect: () => void;
  onFocus: () => void;
  onArrowPress?: (direction: string) => boolean;
}) {
  const {
    ref,
    focused,
    focusKey: key,
  } = useFocusable<HTMLDivElement>({
    focusKey: SEARCH_MORE_FOCUS,
    onEnterPress: onSelect,
    onFocus,
    onArrowPress: (direction) => onArrowPress?.(direction) ?? true,
  });
  return (
    <div
      ref={ref}
      role="button"
      aria-busy={loading || undefined}
      data-focus-key={key}
      data-focused={focused || undefined}
      data-testid={SEARCH_MORE_FOCUS}
      className={cx(styles.moreRow, focused && styles.moreRowFocused)}
    >
      {label}
    </div>
  );
}

/**
 * Search (Figma TV-04). Left: on-screen keyboard + the "search from your phone" QR panel
 * (join URL from `GET /sessions/:sid/qr`). Right: three tabs, all fed by the single
 * `GET /search?q=` call. Song results open the song action dialog (docs/04 §7.5); artists and
 * playlists open the playlist view. No durations, no tier badges (§7.3/§7.7).
 */
export default function Search({ params }: ScreenProps<'search'>) {
  const { t, i18n } = useT();
  const locale = i18n.language === 'en' ? 'en' : 'es';
  const [q, setQ] = useState(params?.q ?? '');
  const [debounced, setDebounced] = useState(params?.q ?? '');
  const [tab, setTab] = useState<Tab>('songs');
  const [focusedRow, setFocusedRow] = useState(0);
  const navigate = useRouter((s) => s.navigate);
  const { openSongActions, overlay } = useSongActions();
  useScreenFocus('search', SEARCH_FOCUS);

  /**
   * UX-046: a tab selects on focus, and ArrowUp from any result row landed on the *leftmost*
   * tab — so Artistas → Down into the results → Up put the user back on "Canciones" and
   * switched the tab under them. Artist and playlist results were browse-only-once. The strip
   * is a focus container now and its preferred child is the **selected** tab, so coming back
   * from the list returns to the tab the results belong to.
   */
  const { ref: tabsRef, focusKey: tabsKey } = useFocusable<HTMLDivElement>({
    focusKey: SEARCH_TABS_FOCUS,
    trackChildren: true,
    saveLastFocusedChild: true,
    preferredChildFocusKey: `search-tab-${tab}`,
  });

  // Debounce from the first character: every keystroke restarts the 120 ms timer.
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(q.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [q]);

  /**
   * UX-047: the catalog holds 143 k songs and the answer is capped at 30 per page, so the song
   * half is paged with `?offset=`. Only the songs page: `artists`/`playlists` are the top 10 of
   * the first page and never change.
   *
   * `songsNextOffset` is **optional** in the contract — a TV running against a deploy that
   * predates UX-047 gets no paging fields at all, `getNextPageParam` then answers `undefined`,
   * `hasNextPage` stays false and the screen behaves exactly as it did before (one page, no
   * "Ver más"). Never make the affordance depend on anything but this.
   */
  const search = useInfiniteQuery({
    queryKey: ['search', debounced],
    initialPageParam: 0,
    queryFn: ({ pageParam }): Promise<SearchResponse> =>
      api.search({ query: { q: debounced, offset: pageParam } }),
    getNextPageParam: (last: SearchResponse) => last.songsNextOffset,
    enabled: debounced.length > 0,
  });

  const pages = search.data?.pages;
  const first = debounced.length > 0 ? (pages?.[0] ?? EMPTY) : EMPTY;
  // Every loaded page of songs, appended — a new page never replaces the list under the focus.
  const songs = useMemo(
    () => (debounced.length > 0 ? (pages ?? []).flatMap((page) => page.songs) : []),
    [pages, debounced],
  );
  const results: SearchResponse = useMemo(() => ({ ...first, songs }), [first, songs]);

  // Mixpanel `search { qLength, results }` (docs/04 §5), once per resolved query — the first
  // page of it, so walking the list does not re-report the same search.
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (!pages?.[0] || reported.current === debounced) return;
    reported.current = debounced;
    const page = pages[0];
    analytics.track('search', {
      qLength: debounced.length,
      results: page.songs.length + page.artists.length + page.playlists.length,
    });
  }, [pages, debounced]);

  const rows: (SongDTO | ArtistDTO | PlaylistDTO)[] = results[tab];
  // The songs tab is the only paged one; the other two always answer their top 10.
  const showMore = tab === 'songs' && rows.length > 0 && search.hasNextPage;
  const loadMore = () => {
    if (search.hasNextPage && !search.isFetchingNextPage) void search.fetchNextPage();
  };

  const { ref: listRef, focusKey: listKey } = useFocusable<HTMLDivElement>({
    focusKey: SEARCH_LIST.containerKey,
    // UX-045/UX-046: an empty scroller must not be a focus target. Norigin parks the focus on a
    // container with no focusable children, and that container is not a managed leaf — the
    // watchdog then reports `out_of_scope` on every press and the keyboard can never be typed on
    // again. While there are no rows the D-pad walks past it (Down from the tabs → the keyboard).
    focusable: listIsFocusable(rows.length),
    trackChildren: true,
    saveLastFocusedChild: true,
  });

  // The rows changed under the focus — a keystroke restarted the query, a tab switched, the list
  // shrank, the last page arrived and took "Ver más" with it (UX-047). Only a focus that is
  // *inside* the list is moved; typing never drags the user away.
  useEffect(() => {
    const target = listFocusRepair(getCurrentFocusKey(), rows.length, SEARCH_LIST, {
      hasMore: showMore,
    });
    if (target) void setFocus(target);
  }, [rows.length, tab, showMore]);

  // Walking to the bottom of the loaded songs pulls the next page in before the user gets
  // there; the "Ver más" row stays as the explicit, keyboard-reachable control (UX-047).
  // Sitting *on* that row (`focusedRow === rows.length`) never prefetches: it is refocused
  // after every append, which would otherwise walk the whole catalog on its own.
  useEffect(() => {
    if (tab !== 'songs' || !search.hasNextPage || search.isFetchingNextPage) return;
    if (focusedRow < rows.length && focusedRow >= rows.length - PREFETCH_MARGIN) {
      void search.fetchNextPage();
    }
  }, [focusedRow, rows.length, tab, search]);

  // "Ver más" is one more focusable stop at the end of the list, so it is part of the window.
  const count = rows.length + (showMore ? 1 : 0);
  const win = computeWindow({
    count,
    itemSize: 96,
    gap: 8,
    viewport: 824,
    // A tab switch can leave the remembered row past the end of the shorter list.
    focusedIndex: Math.min(focusedRow, Math.max(count - 1, 0)),
  });

  /**
   * Left/Right from inside a result list hop to the neighbouring tab and land on its first
   * result, so the three lists can be walked without going back up to the strip. Right off the
   * last tab stays put; Left off the first tab still leaves for the keyboard column.
   */
  const hopTab = (direction: string): boolean => {
    if (direction !== 'left' && direction !== 'right') return true;
    const next = TABS.indexOf(tab) + (direction === 'right' ? 1 : -1);
    if (next < 0) return true;
    if (next >= TABS.length) return false;
    const target = TABS[next]!;
    setTab(target);
    setFocusedRow(0);
    window.setTimeout(() => {
      void setFocus(results[target].length > 0 ? 'search-result-0' : `search-tab-${target}`);
    }, 0);
    return false;
  };

  const tabLabel = (key: Tab) =>
    t('tv:search.tabCount', { tab: t(`tv:search.tabs.${key}`), count: results[key].length });
  const cue = t('tv:playlist.cue');

  return (
    <>
      <div className={styles.content} data-testid="screen-search">
        <div className={styles.searchLeft}>
          <div className={styles.searchBox} data-testid="search-query">
            <span className={styles.icon}>
              <IconSearch size={36} />
            </span>
            {q ? (
              <span>{q}</span>
            ) : (
              <span className={styles.placeholder}>{t('tv:search.placeholder')}</span>
            )}
            <span className={styles.caret} />
          </div>
          <OnScreenKeyboard
            value={q}
            onChange={(next) => {
              setQ(next);
              setFocusedRow(0);
            }}
            // UX-048: digits (song titles carry them — "Aventura 2"), and the line that says
            // typing CORAZON is enough to find "Corazón".
            digits
            labels={{
              space: t('tv:search.keyboard.space'),
              backspace: t('tv:search.keyboard.backspace'),
              clear: t('tv:search.keyboard.clear'),
              hint: t('tv:search.keyboard.hint'),
            }}
          />
          {/* The shared "send songs from your phone" panel (also on Home and in the rail): a
              square 200 px QR that no longer gets squeezed thin by the sentence beside it. */}
          <PhoneQr className={styles.searchQr} data-testid="search-qr" />
        </div>
        <div className={styles.searchRight}>
          <FocusContext.Provider value={tabsKey}>
            <div ref={tabsRef} data-testid="search-tabs" className={styles.tabs} role="tablist">
              {TABS.map((key) => (
                <TabButton
                  key={key}
                  tab={key}
                  label={tabLabel(key)}
                  active={tab === key}
                  onSelect={() => {
                    setTab(key);
                    setFocusedRow(0);
                  }}
                  onArrowPress={(direction) => {
                    // Left/Right walk the strip and open the neighbour's results on the spot
                    // (a tab selects on focus). Left off the first tab still leaves for the
                    // keyboard column; Right off the last one stays put.
                    const at = TABS.indexOf(key);
                    if (direction === 'left' && at > 0) {
                      void setFocus(`search-tab-${TABS[at - 1]!}`);
                      return false;
                    }
                    if (direction === 'right') {
                      if (at < TABS.length - 1) void setFocus(`search-tab-${TABS[at + 1]!}`);
                      return false;
                    }
                    return true;
                  }}
                />
              ))}
            </div>
          </FocusContext.Provider>
          <FocusContext.Provider value={listKey}>
            <div ref={listRef} data-testid="search-results" className={styles.searchList}>
              <div
                className={styles.scroller}
                style={{ transform: `translateY(${-win.offset}px)` }}
              >
                {!debounced ? (
                  <div className={styles.empty}>{t('tv:search.empty')}</div>
                ) : search.isError ? (
                  <div className={styles.empty} role="alert">
                    {t('tv:search.loadError')}
                  </div>
                ) : search.isPending ? (
                  <div className={styles.empty}>{t('common:states.loading')}</div>
                ) : rows.length === 0 ? (
                  // UX-003: one sentence per tab. Interpolating the tab label put a capital
                  // noun mid-sentence ("No hay Canciones para…") in Spanish and in English.
                  <div className={styles.empty}>
                    {t(`tv:search.noResultsIn.${tab}`, { q: debounced })}
                  </div>
                ) : (
                  <>
                    {rows.slice(0, win.end).map((item, index) => {
                      const focusKey = `search-result-${index}`;
                      if (tab === 'songs') {
                        const song = item as SongDTO;
                        return (
                          <ResultRow
                            key={song.id}
                            focusKey={focusKey}
                            title={song.title}
                            subtitle={formatArtists(song.artists, locale)}
                            color={colorFor(song.id)}
                            imageUrl={song.imageUrl}
                            cue={cue}
                            onFocus={() => setFocusedRow(index)}
                            onSelect={() => openSongActions(song)}
                            onArrowPress={hopTab}
                          />
                        );
                      }
                      if (tab === 'playlists') {
                        const playlist = item as PlaylistDTO;
                        return (
                          <ResultRow
                            key={playlist.id}
                            focusKey={focusKey}
                            title={pickI18n(playlist.name, locale)}
                            subtitle={t('tv:playlist.songs', { count: playlist.total })}
                            color={colorFor(playlist.id)}
                            imageUrl={playlist.imageUrl}
                            onFocus={() => setFocusedRow(index)}
                            onSelect={() => navigate('playlist', playlistTarget(playlist))}
                            onArrowPress={hopTab}
                          />
                        );
                      }
                      const artist = item as ArtistDTO;
                      return (
                        <ResultRow
                          key={artist.id}
                          focusKey={focusKey}
                          title={artist.name}
                          subtitle={
                            artist.songCount === undefined
                              ? undefined
                              : t('tv:playlist.songs', { count: artist.songCount })
                          }
                          color={colorFor(artist.id)}
                          imageUrl={artist.imageUrl}
                          onFocus={() => setFocusedRow(index)}
                          onSelect={() => navigate('playlist', { id: artist.id, kind: 'artist' })}
                          onArrowPress={hopTab}
                        />
                      );
                    })}
                    {/* UX-047: only rendered when the API says songs exist past this page, so
                        a TV talking to a deploy without paging shows nothing new. */}
                    {showMore ? (
                      <MoreRow
                        label={
                          search.isFetchingNextPage
                            ? t('tv:search.moreLoading')
                            : t('tv:search.more')
                        }
                        loading={search.isFetchingNextPage}
                        onSelect={loadMore}
                        onFocus={() => setFocusedRow(rows.length)}
                        onArrowPress={hopTab}
                      />
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </FocusContext.Provider>
        </div>
      </div>
      {overlay}
    </>
  );
}
